/**
 * CommandPalette — global ⌘K / Ctrl-K search over everything the
 * current user is allowed to see. Grouped results per resource type
 * (Shifts, Medics, Contracts, Users, Templates, Job Types), fuzzy
 * substring match, keyboard-first (cmdk handles ↑↓ Enter Esc).
 *
 * Iter72.7 — complements the Recently viewed card: returning to a
 * record you *didn't* just open becomes a two-keystroke habit.
 *
 * Permissions model: we don't re-implement RBAC on the client. We
 * just fire every list endpoint; the backend returns 200 with filtered
 * data for permitted callers and 403/404 for the rest. Groups whose
 * fetch failed are silently dropped — a medic naturally sees only
 * Shifts, an admin sees all six groups.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from './ui/command';
import { Calendar, Users, FileText, Layers, Briefcase, User as UserIcon, Search } from 'lucide-react';
import {
  shiftsAPI, medicsAPI, contractsAPI, templatesAPI, jobTypesAPI, usersAPI,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { trackRecentView } from '../hooks/useRecentlyViewed';

/** Group definitions — label, icon, API call, map to item shape. */
const GROUPS = [
  {
    key: 'shifts',
    label: 'Shifts',
    icon: Calendar,
    fetch: () => shiftsAPI.list({}),
    toItem: (s) => ({
      id: s.id,
      type: 'shift',
      title: s.location || 'Shift',
      subtitle: `${s.date} · ${s.start_time}–${s.end_time}${s.medic_name ? ' · ' + s.medic_name : ' · Unassigned'}`,
      searchBlob: `${s.location || ''} ${s.date || ''} ${s.medic_name || ''} ${s.postcode || ''} ${s.address || ''} ${s.status || ''}`.toLowerCase(),
      href: '/shifts',
      recent: { label: `${s.location || 'Shift'} · ${s.date}`, subtitle: s.medic_name || 'Unassigned' },
    }),
  },
  {
    key: 'medics',
    label: 'Medics',
    icon: Users,
    fetch: () => medicsAPI.list({}),
    toItem: (m) => ({
      id: m.id,
      type: 'medic',
      title: m.name || 'Unnamed medic',
      subtitle: m.role ? `${m.role}${m.email ? ' · ' + m.email : ''}` : (m.email || ''),
      searchBlob: `${m.name || ''} ${m.email || ''} ${m.role || ''} ${m.phone || ''}`.toLowerCase(),
      href: '/medics',
      recent: { label: m.name || 'Unnamed medic', subtitle: m.role || m.email },
    }),
  },
  {
    key: 'contracts',
    label: 'Contracts',
    icon: FileText,
    fetch: () => contractsAPI.list(),
    toItem: (c) => ({
      id: c.id,
      type: 'contract',
      title: c.name,
      subtitle: c.description || `${c.start_date || ''} → ${c.end_date || ''}`,
      searchBlob: `${c.name || ''} ${c.description || ''}`.toLowerCase(),
      href: '/contracts',
      recent: { label: c.name, subtitle: c.description },
    }),
  },
  {
    key: 'users',
    label: 'Users',
    icon: UserIcon,
    fetch: () => usersAPI.list(),
    toItem: (u) => ({
      id: u.id,
      type: 'user',
      title: u.name || u.email,
      subtitle: Array.isArray(u.roles) ? u.roles.join(', ') : (u.role || u.email),
      searchBlob: `${u.name || ''} ${u.email || ''} ${(u.roles || []).join(' ')}`.toLowerCase(),
      href: '/medics',
      recent: { label: u.name || u.email, subtitle: (u.roles || []).join(', ') || u.email },
    }),
  },
  {
    key: 'templates',
    label: 'Templates',
    icon: Layers,
    fetch: () => templatesAPI.list(),
    toItem: (t) => ({
      id: t.id,
      type: 'template',
      title: t.name || 'Untitled template',
      subtitle: `${t.start_time}–${t.end_time}${t.default_location ? ' · ' + t.default_location : ''}`,
      searchBlob: `${t.name || ''} ${t.default_location || ''} ${t.default_postcode || ''}`.toLowerCase(),
      href: '/shifts',
      recent: { label: t.name || 'Untitled template', subtitle: `${t.start_time}–${t.end_time}` },
    }),
  },
  {
    key: 'jobTypes',
    label: 'Job Types',
    icon: Briefcase,
    fetch: () => jobTypesAPI.list({}),
    toItem: (j) => ({
      id: j.id,
      type: 'jobType',
      title: j.name,
      subtitle: j.contract_name || '',
      searchBlob: `${j.name || ''} ${j.contract_name || ''}`.toLowerCase(),
      href: '/job-types',
    }),
  },
];

/** Fetch every group in parallel. Failed groups are dropped (403/404). */
async function loadAll() {
  const settled = await Promise.allSettled(GROUPS.map(g => g.fetch()));
  const out = {};
  settled.forEach((r, i) => {
    const g = GROUPS[i];
    if (r.status !== 'fulfilled') return;
    const rows = Array.isArray(r.value?.data) ? r.value.data : [];
    out[g.key] = rows.map(g.toItem);
  });
  return out;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const navigate = useNavigate();

  // Global keyboard shortcut (⌘K / Ctrl-K) and a custom event so the
  // Header's Search pill can open the same palette with one line.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    const onCustom = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('elara:open-command-palette', onCustom);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('elara:open-command-palette', onCustom);
    };
  }, []);

  // Fetch on first open (and on every re-open — cheap, data is small).
  useEffect(() => {
    if (!open || !isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    loadAll().then((data) => {
      if (!cancelled) {
        setGroups(data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [open, isAuthenticated]);

  // Fuzzy filter — plain substring, case-insensitive. cmdk's own
  // filter is fine for < 500 rows per group.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    const out = {};
    for (const [key, items] of Object.entries(groups)) {
      const hits = (items || []).filter(i => i.searchBlob.includes(q));
      if (hits.length) out[key] = hits.slice(0, 8);
    }
    return out;
  }, [groups, query]);

  const handleSelect = useCallback((item) => {
    if (item.recent && user?.id) {
      trackRecentView(user.id, {
        type: item.type,
        id: item.id,
        label: item.recent.label,
        subtitle: item.recent.subtitle,
        href: item.href,
      });
    }
    setOpen(false);
    setQuery('');
    navigate(item.href);
  }, [navigate, user?.id]);

  // Only *render the UI* when authenticated. Hooks above must still
  // run every render (React rules), so this early return comes last.
  if (!isAuthenticated) return null;

  const totalHits = Object.values(filtered).reduce((n, items) => n + (items?.length || 0), 0);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search shifts, medics, contracts…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList>
        {loading && <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && totalHits === 0 && (
          <CommandEmpty>
            {query ? `No matches for "${query}"` : 'Nothing in reach yet — start typing.'}
          </CommandEmpty>
        )}
        {GROUPS.map((g) => {
          const items = filtered[g.key];
          if (!items || !items.length) return null;
          const Icon = g.icon;
          return (
            <CommandGroup key={g.key} heading={g.label}>
              {items.map((item) => (
                <CommandItem
                  key={`${g.key}:${item.id}`}
                  value={`${g.key}-${item.id}-${item.title}`}
                  onSelect={() => handleSelect(item)}
                  data-testid={`cmdk-item-${item.type}-${item.id}`}
                >
                  <Icon className="w-4 h-4 mr-2 text-[#1ABC9C]" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{item.title}</div>
                    {item.subtitle && <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
      <div className="flex items-center justify-between px-3 py-2 border-t border-white/5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Search className="w-3 h-3" />Search everywhere you can access</span>
        <span>↑↓ navigate · <CommandShortcut>↵</CommandShortcut> open · <CommandShortcut>Esc</CommandShortcut> close</span>
      </div>
    </CommandDialog>
  );
}

export default CommandPalette;
